import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Megaphone, Film, Users, ArrowRight, X, Play } from 'lucide-react';
import { clientBrands } from '../mock';

const portfolioPreview = {
  'ugc': [
    { type: 'youtube', src: 'https://www.youtube.com/embed/K7PpjYO3CcA', title: 'UGC Short', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/fI5-toYIw-M', title: 'UGC Content', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/gPq_KCW7sAc', title: 'UGC Reel', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/2rEqYCfgu-M', title: 'UGC Video', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/uUH5W0QbSfI', title: 'UGC Video', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/mIwt7huXC4Q', title: 'UGC Video', aspect: 'portrait' },
  ],
  'video-editing': [
    { type: 'youtube', src: 'https://www.youtube.com/embed/mqLiBAhh800', title: 'Brand Edit', aspect: 'landscape' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/K1BKmnCrZDY', title: 'Content Edit', aspect: 'landscape' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/Rypk89DQF4M?start=2', title: 'Video Edit', aspect: 'landscape' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/oxiXNBTLVhw', title: 'Highlight Edit', aspect: 'landscape' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/E3JvoqMzwkQ', title: 'Video Edit', aspect: 'landscape' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/luCntDwXrbQ?start=16', title: 'Video Edit', aspect: 'landscape' },
  ],
  'social-media': [
    { type: 'youtube', src: 'https://www.youtube.com/embed/vSM0kivw8VI', title: 'Social Reel 1', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/7T1jGgrFG3c', title: 'Social Reel 2', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/atdWmggoUM4', title: 'Social Reel 3', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/I03lYISGK2g', title: 'Social Reel 4', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/E21iHhLwbFU', title: 'Social Reel 5', aspect: 'portrait' },
    { type: 'youtube', src: 'https://www.youtube.com/embed/zFvuxhKXv2Q', title: 'Social Reel 6', aspect: 'portrait' },
  ]
};

const tabs = [
  { id: 'ugc', label: 'UGC' },
  { id: 'video-editing', label: 'Video Editing' },
  { id: 'social-media', label: 'Social Media' },
];

const Home = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeTab, setActiveTab] = useState('ugc');
  const [popupItem, setPopupItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const currentItems = portfolioPreview[activeTab] || [];
  const isLandscapeTab = activeTab === 'video-editing';

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center animate-fade-in">
            <div className="inline-block mb-6">
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 px-4 py-2 text-sm font-semibold">
                Creative Agency for Modern Brands
              </Badge>
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-black text-white mb-6 leading-tight tracking-tighter animate-slide-up">
              Content That
              <span className="block bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent">
                Converts.
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed animate-slide-up-delay">We create scroll-stopping social content, world-class video editing, and authentic user generated content (UGC) that transforms startups and e-commerce brands into category leaders.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up-delay-2">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg px-8 py-6 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                  Book a Discovery Call <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 font-bold text-lg px-8 py-6 transition-all hover:scale-105">
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services CTA Section - MOVED UP above Brands */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <Card className="bg-slate-900/60 border-amber-500/30 backdrop-blur-sm shadow-2xl shadow-amber-500/10 overflow-hidden">
              <CardContent className="p-12 md:p-16">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">
                      Our Expertise
                    </Badge>
                    <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                      Three Services. <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">One Goal.</span>
                    </h2>
                    <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                      We specialize in social media content, video editing, and UGC production. Each service is designed to help your brand cut through the noise and drive real results.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/editing">
                        <Button
                          className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all hover:scale-105">
                          View Pricing <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/contact">
                        <Button
                          variant="outline"
                          className="border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 font-bold px-6 py-2 transition-all hover:scale-105">
                          Book a Call
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-400 rounded-lg flex items-center justify-center mb-4">
                        <Megaphone className="w-6 h-6 text-slate-950" />
                      </div>
                      <h3 className="text-white font-bold mb-2">Social Media</h3>
                      <p className="text-slate-400 text-sm">Scroll-stopping content & ads</p>
                    </div>
                    <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-400 rounded-lg flex items-center justify-center mb-4">
                        <Film className="w-6 h-6 text-slate-950" />
                      </div>
                      <h3 className="text-white font-bold mb-2">Video Editing</h3>
                      <p className="text-slate-400 text-sm">Professional post-production</p>
                    </div>
                    <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all sm:col-span-2">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-400 rounded-lg flex items-center justify-center mb-4">
                        <Users className="w-6 h-6 text-slate-950" />
                      </div>
                      <h3 className="text-white font-bold mb-2">UGC Production</h3>
                      <p className="text-slate-400 text-sm">Authentic creator content that converts</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Brands We Work With - MOVED DOWN */}
      <section className="py-32 overflow-hidden" data-animate>
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">Our Clients</Badge>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
              Brands We <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Work With</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Trusted by innovative brands across industries
            </p>
          </div>

          {/* Auto-scrolling Carousel */}
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-950 to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-950 to-transparent z-10" />

            <div className="carousel-container">
              <div className="carousel-track">
                {clientBrands.map((brand, index) =>
                  <div
                    key={`brand-1-${index}`}
                    className="carousel-item bg-slate-900/50 border border-slate-800 backdrop-blur-sm rounded-xl p-8 flex items-center justify-center min-w-[280px] h-[140px] mx-3">
                    {brand.logo ?
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all" /> :
                      <p className="text-white font-bold text-lg text-center leading-tight">
                        {brand.name}
                      </p>
                    }
                  </div>
                )}
                {clientBrands.map((brand, index) =>
                  <div
                    key={`brand-2-${index}`}
                    className="carousel-item bg-slate-900/50 border border-slate-800 backdrop-blur-sm rounded-xl p-8 flex items-center justify-center min-w-[280px] h-[140px] mx-3">
                    {brand.logo ?
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all" /> :
                      <p className="text-white font-bold text-lg text-center leading-tight">
                        {brand.name}
                      </p>
                    }
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
          </div>
        </div>
      </section>

      {/* Portfolio Preview Section */}
      <section className="py-20 relative" data-animate>
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">Our Work</Badge>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
              What We <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Create</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              A glimpse of the content we produce for our clients
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-8 py-3 rounded-full font-bold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 shadow-lg shadow-amber-500/30 scale-105'
                    : 'bg-slate-800/50 text-slate-300 border border-slate-700 hover:border-amber-500/50 hover:text-amber-400'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Thumbnail Grid */}
          <div className={`max-w-6xl mx-auto ${isLandscapeTab ? 'max-w-5xl' : 'max-w-5xl'}`}>
            <div className={`grid gap-4 md:gap-6 ${
              isLandscapeTab
                ? 'grid-cols-1 sm:grid-cols-2'
                : 'grid-cols-2 md:grid-cols-4'
            }`}>
              {currentItems.map((item, index) => {
                const videoId = item.type === 'youtube' ? item.src.split('/embed/')[1]?.split('?')[0] : null;
                const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
                return (
                  <div
                    key={`${activeTab}-${index}`}
                    className="cursor-pointer group"
                    onClick={() => setPopupItem(item)}
                  >
                    <div className={`relative bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10 ${
                      isLandscapeTab ? 'aspect-video' : 'aspect-[9/16]'
                    }`}>
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <iframe
                          src={item.src}
                          className="w-full h-full border-0 pointer-events-none"
                          title={item.title}
                          scrolling="no"
                          allowFullScreen
                          style={{ border: 'none', overflow: 'hidden' }}
                        />
                      )}
                      <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                          <Play className="w-7 h-7 text-slate-950 ml-0.5" />
                        </div>
                      </div>
                    </div>
                    <p className="text-white font-semibold text-sm mt-3 text-center">{item.title}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* View All Work Button */}
          <div className="text-center mt-12">
            <Link to="/portfolio">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 font-bold text-lg px-8 py-6 transition-all hover:scale-105">
                View All Our Work <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Popup Modal for Portfolio Item */}
      {popupItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4"
          onClick={() => setPopupItem(null)}
        >
          <div
            className={`relative bg-slate-900 rounded-2xl border border-amber-500/30 shadow-2xl shadow-amber-500/10 overflow-hidden mx-4 ${
              popupItem.aspect === 'portrait'
                ? 'max-w-sm w-full'
                : 'max-w-4xl w-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPopupItem(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-800/80 text-white hover:bg-amber-500/20 hover:text-amber-400 flex items-center justify-center transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <div className={`w-full ${
              popupItem.aspect === 'portrait'
                ? 'aspect-[9/16] max-h-[80vh]'
                : 'aspect-video'
            }`}>
              <iframe
                src={popupItem.src}
                className="w-full h-full border-0"
                title={popupItem.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                scrolling="no"
                style={popupItem.type === 'facebook' ? { border: 'none', overflow: 'hidden' } : undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bottom CTA - Book a Call + Pricing */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6 relative z-10">
          <Card className="bg-slate-900/60 border-amber-500/30 backdrop-blur-sm shadow-2xl shadow-amber-500/10 max-w-4xl mx-auto">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl font-black text-white mb-4">
                Let's Build Something <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Together</span>
              </h2>
              <p className="text-xl text-slate-400 mb-8">
                Book a free discovery call and let's discuss how we can help your brand grow.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg px-8 py-6 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                    Book a Discovery Call <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/editing">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 font-bold text-lg px-8 py-6 transition-all hover:scale-105">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
